import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  ContentCopy as CopyIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useCompany } from '../contexts/CompanyContext';

interface Content {
  id: number;
  title: string;
  content: string;
  content_type: string;
  keyword: string;
  created_at: string;
}

const ContentManagement: React.FC = () => {
  const { selectedCompany } = useCompany();
  const [contents, setContents] = useState<Content[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedContent, setSelectedContent] = useState<Content | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contentToDelete, setContentToDelete] = useState<Content | null>(null);
  const [filterType, setFilterType] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (selectedCompany) {
      fetchContents();
    }
  }, [selectedCompany]);

  const fetchContents = async () => {
    if (!selectedCompany) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`/api/content/list?companyId=${selectedCompany.id}`);
      setContents(response.data);
    } catch (error) {
      console.error('Error fetching contents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleView = (content: Content) => {
    setSelectedContent(content);
    setViewDialogOpen(true);
  };

  const handleCopy = (content: Content) => {
    navigator.clipboard.writeText(content.content);
    setSuccessMessage('Content copied to clipboard!');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleDeleteClick = (content: Content) => {
    setContentToDelete(content);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!contentToDelete) return;

    try {
      await axios.delete(`/api/content/${contentToDelete.id}`);
      setSuccessMessage('Content deleted successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      setDeleteDialogOpen(false);
      setContentToDelete(null);
      fetchContents(); // Refresh list
    } catch (error) {
      console.error('Error deleting content:', error);
    }
  };

  const getContentTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      instagram: 'Instagram',
      facebook: 'Facebook',
      linkedin: 'LinkedIn',
      twitter: 'X/Twitter',
      blog_post: 'Blog Post',
      email: 'Email',
      ad_copy: 'Ad Copy',
      subject_line: 'Subject Line',
    };
    return labels[type] || type;
  };

  const getContentTypeColor = (type: string) => {
    const colors: { [key: string]: 'primary' | 'secondary' | 'success' | 'warning' | 'info' | 'error' } = {
      instagram: 'error',
      facebook: 'primary',
      linkedin: 'info',
      twitter: 'info',
      blog_post: 'success',
      email: 'warning',
      ad_copy: 'secondary',
      subject_line: 'primary',
    };
    return colors[type] || 'default';
  };

  const filteredContents = contents.filter(content => {
    const matchesType = filterType === 'all' || content.content_type === filterType;
    const matchesSearch = content.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          content.keyword.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesType && matchesSearch;
  });

  if (!selectedCompany) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Please select a company to view content.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          Content Library
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {selectedCompany.name}
        </Typography>
      </Box>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by title or keyword..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label="All"
                onClick={() => setFilterType('all')}
                color={filterType === 'all' ? 'primary' : 'default'}
                icon={<FilterIcon />}
              />
              <Chip
                label="Instagram"
                onClick={() => setFilterType('instagram')}
                color={filterType === 'instagram' ? 'primary' : 'default'}
              />
              <Chip
                label="LinkedIn"
                onClick={() => setFilterType('linkedin')}
                color={filterType === 'linkedin' ? 'primary' : 'default'}
              />
              <Chip
                label="Blog"
                onClick={() => setFilterType('blog_post')}
                color={filterType === 'blog_post' ? 'primary' : 'default'}
              />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : filteredContents.length === 0 ? (
        <Paper sx={{ p: 8, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No content found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {searchQuery || filterType !== 'all'
              ? 'Try adjusting your filters or search query.'
              : 'Generate some content to get started!'}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {filteredContents.map((content) => (
            <Grid item xs={12} md={6} lg={4} key={content.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                    <Chip
                      label={getContentTypeLabel(content.content_type)}
                      color={getContentTypeColor(content.content_type)}
                      size="small"
                    />
                    <Typography variant="caption" color="text.secondary">
                      {new Date(content.created_at).toLocaleDateString()}
                    </Typography>
                  </Box>
                  
                  <Typography variant="h6" sx={{ mb: 1, minHeight: '3em' }}>
                    {content.title}
                  </Typography>
                  
                  <Chip label={content.keyword} size="small" variant="outlined" sx={{ mb: 2 }} />
                  
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {content.content}
                  </Typography>
                </CardContent>
                
                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                  <Box>
                    <IconButton size="small" onClick={() => handleView(content)} title="View">
                      <ViewIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleCopy(content)} title="Copy">
                      <CopyIcon />
                    </IconButton>
                  </Box>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteClick(content)}
                    title="Delete"
                  >
                    <DeleteIcon />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* View Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{selectedContent?.title}</Typography>
            <Chip
              label={selectedContent ? getContentTypeLabel(selectedContent.content_type) : ''}
              color={selectedContent ? getContentTypeColor(selectedContent.content_type) : 'default'}
              size="small"
            />
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Keyword: {selectedContent?.keyword}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
              Created: {selectedContent ? new Date(selectedContent.created_at).toLocaleString() : ''}
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {selectedContent?.content}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleCopy(selectedContent!)} startIcon={<CopyIcon />}>
            Copy Content
          </Button>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Content?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{contentToDelete?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContentManagement;

